import Highcharts from "highcharts";
import * as io from "io-ts";
import { useCallback, useMemo, useRef } from "react";
import createGlobalState from "@ui/lib/createGlobalState";
import type { Options } from "@ui/Highcharts";
import type { BlockPlotModelT } from "../../../../../../schema/block/plot";
import { hideRulePlotLinesOrBands, showRulePlotLinesOrBands } from "../utils/rule";

const useDashboardHiddenSerieList = createGlobalState({
  key: "dashboardHiddenSerieList",
  storage: "session",
  scoped: true,
  defaultValue: [],
  type: io.array(io.string),
});

export function useWithHiddenSeries(block: BlockPlotModelT, options: Options | null) {
  const [hiddenSerieIds, setHiddenSerieIds] = useDashboardHiddenSerieList({ scope: block.id });
  const hiddenSerieIdsRef = useRef(hiddenSerieIds);

  const hideWithHiddenSeries = useCallback(
    function (this: Highcharts.Series) {
      hideRulePlotLinesOrBands(this);
      setHiddenSerieIds(hiddenSerieIds => {
        if (!this.userOptions.id) return hiddenSerieIds;
        return Array.from(new Set([...hiddenSerieIds, this.userOptions.id]));
      });
    },
    [setHiddenSerieIds],
  );

  const showWithHiddenSeries = useCallback(
    function (this: Highcharts.Series) {
      showRulePlotLinesOrBands(this);
      setHiddenSerieIds(hiddenSerieIds => {
        if (!this.userOptions.id) return hiddenSerieIds;
        return hiddenSerieIds.filter(id => id !== this.userOptions.id);
      });
    },
    [setHiddenSerieIds],
  );

  return useMemo(() => {
    if (!options) return null;
    return {
      ...options,
      series: options.series?.map(serie => {
        serie.visible = serie.id ? !hiddenSerieIdsRef.current.includes(serie.id) : true;
        serie.events ??= {};
        serie.events.hide = hideWithHiddenSeries;
        serie.events.show = showWithHiddenSeries;
        return serie;
      }),
    };
  }, [hideWithHiddenSeries, options, showWithHiddenSeries]);
}
